import PropTypes from "prop-types";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import useClickOutside from "../../../../hooks/useClickOutside";
import useIsTruncated from "../../../../hooks/useIsTruncated";
import useSmoothExpand from "../../../../hooks/useSmoothExpand";
import { computeOverlayListPosition } from "../../../../hooks/useDropdownOverlay";
import ConfirmationModal from "../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../parts/SvgIcon";

const DESCRIPTION_COLLAPSED_LINES = 2;

const MENU_ACTIONS = {
	OPEN: "open",
	MANAGE: "manage",
	EDIT: "edit",
	DUPLICATE: "duplicate",
	DELETE: "delete",
};

const WorkoutFolderCard = ({
	folder,
	workoutCount = 0,
	descriptionExpanded = false,
	onToggleDescriptionExpanded,
}) => {
	const { t } = useTranslation("coaching-portal");
	const [menuOpen, setMenuOpen] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [listPosition, setListPosition] = useState(null);
	const menuRef = useRef(null);
	const listRef = useRef(null);
	const [descriptionRef, isDescriptionTruncated] = useIsTruncated();
	useSmoothExpand(
		descriptionRef,
		descriptionExpanded,
		DESCRIPTION_COLLAPSED_LINES,
	);

	// Latch: once a description is known to overflow, keep the toggle visible
	// even after it's expanded (isTruncated reads false once unclamped), so the
	// user can always collapse it back.
	const wasDescriptionExpandable = useRef(false);
	if (isDescriptionTruncated) {
		wasDescriptionExpandable.current = true;
	}
	const canToggleDescription =
		wasDescriptionExpandable.current || descriptionExpanded;

	const closeMenu = useCallback(() => setMenuOpen(false), []);

	useClickOutside(
		menuRef,
		closeMenu,
		menuOpen,
		listRef.current ? [listRef.current] : [],
	);

	// Position the menu list via a portal (like Dropdown's `overlay` mode) so
	// it isn't trapped inside the card's stacking context when :hover applies
	// a filter, which otherwise makes later grid siblings render on top of it.
	useLayoutEffect(() => {
		if (!menuOpen || !menuRef.current) {
			setListPosition(null);
			return undefined;
		}

		const placeList = () => {
			const rect = menuRef.current.getBoundingClientRect();
			const listWidth =
				listRef.current?.getBoundingClientRect().width ?? rect.width;
			setListPosition(
				computeOverlayListPosition({
					rect,
					listWidth,
					alignRight: true,
					openUpward: false,
				}),
			);
		};

		placeList();

		if (!listRef.current) {
			const frameId = requestAnimationFrame(placeList);
			return () => cancelAnimationFrame(frameId);
		}

		return undefined;
	}, [menuOpen]);

	useEffect(() => {
		if (!menuOpen) return undefined;
		const handleScroll = (e) => {
			if (listRef.current?.contains(e.target)) return;
			closeMenu();
		};
		window.addEventListener("scroll", handleScroll, true);
		window.addEventListener("resize", closeMenu);
		return () => {
			window.removeEventListener("scroll", handleScroll, true);
			window.removeEventListener("resize", closeMenu);
		};
	}, [menuOpen, closeMenu]);

	const openFolder = () => {
		submit("/actions/refresh", {
			method: "GET",
			body: {
				folderId: folder._id,
				tabView: "single-workouts-tab",
				templateView: "single-workouts",
			},
		});
	};

	const handleCardKeyDown = (e) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			openFolder();
		}
	};

	const handleMenuAction = (action) => {
		setMenuOpen(false);
		switch (action) {
			case MENU_ACTIONS.OPEN:
				openFolder();
				break;
			case MENU_ACTIONS.MANAGE:
				submit("/actions/refresh", {
					method: "GET",
					body: {
						"modal[type]": "manage-workout-folder",
						targetFolderId: folder._id,
					},
				});
				break;
			case MENU_ACTIONS.EDIT:
				submit("/actions/refresh", {
					method: "GET",
					body: {
						"modal[type]": "handle-workout-folder",
						targetFolderId: folder._id,
					},
				});
				break;
			case MENU_ACTIONS.DUPLICATE:
				submit("/actions/workout-folders/duplicate", {
					method: "POST",
					body: { folderId: folder._id },
				});
				break;
			case MENU_ACTIONS.DELETE:
				setConfirmDelete(true);
				break;
			default:
				break;
		}
	};

	const confirmDeleteFolder = () => {
		setConfirmDelete(false);
		submit("/actions/workout-folders/remove", {
			method: "POST",
			body: { id: folder._id },
		});
	};

	const menuItems = [
		{
			value: MENU_ACTIONS.OPEN,
			label: t("allTemplates.workoutFolders.menu.openFolder", "Open Folder"),
		},
		{
			value: MENU_ACTIONS.MANAGE,
			label: t(
				"allTemplates.workoutFolders.menu.manageWorkouts",
				"Manage Workouts",
			),
		},
		{
			value: MENU_ACTIONS.EDIT,
			label: t(
				"allTemplates.workoutFolders.menu.editNameDescription",
				"Edit Name & Description",
			),
		},
		// {
		// 	value: MENU_ACTIONS.DUPLICATE,
		// 	label: t("allTemplates.workoutFolders.menu.duplicate", "Duplicate"),
		// },
		{
			value: MENU_ACTIONS.DELETE,
			label: t(
				"allTemplates.workoutFolders.menu.deleteFolder",
				"Delete Folder",
			),
			danger: true,
		},
	];

	return (
		<>
			{/* biome-ignore lint/a11y/useSemanticElements: can't be a <button> since it wraps the folder-menu <button> */}
			<div
				className="workout-folder-card"
				role="button"
				tabIndex={0}
				onClick={openFolder}
				onKeyDown={handleCardKeyDown}
			>
				<div className="workout-folder-card-top">
					<div className="workout-folder-card-main">
						<SvgIcon
							src="/img/new-icons/folder.svg"
							classNames="workout-folder-card-icon"
						/>
						<div className="workout-folder-card-text">
							<span className="workout-folder-card-name">{folder.name}</span>
							<span
								ref={descriptionRef}
								className={
									folder?.description
										? "workout-folder-card-description"
										: "workout-folder-card-description workout-folder-card-description--empty"
								}
							>
								{folder?.description || ""}
							</span>
						</div>
					</div>
					<div className="workout-folder-card-menu" ref={menuRef}>
						<button
							type="button"
							className="workout-folder-card-menu-trigger"
							onClick={(e) => {
								e.stopPropagation();
								setMenuOpen((open) => !open);
							}}
							aria-label={t(
								"allTemplates.workoutFolders.menuAlt",
								"Folder menu",
							)}
						>
							<SvgIcon src="/img/new-icons/more-vertical.svg" />
						</button>
						{menuOpen &&
							listPosition &&
							createPortal(
								<ul
									ref={listRef}
									className="workout-folder-card-menu-list"
									style={{
										position: "fixed",
										...(listPosition.top != null
											? { top: listPosition.top }
											: {}),
										...(listPosition.bottom != null
											? { bottom: listPosition.bottom }
											: {}),
										...(listPosition.left != null
											? { left: listPosition.left }
											: {}),
										...(listPosition.right != null
											? { right: listPosition.right }
											: {}),
										minWidth: listPosition.minWidth,
										maxHeight: listPosition.maxHeight,
										zIndex: 10000,
									}}
								>
									{menuItems.map((item) => (
										<li key={item.value}>
											<button
												type="button"
												className={item.danger ? "danger" : undefined}
												onClick={(e) => {
													e.stopPropagation();
													handleMenuAction(item.value);
												}}
											>
												{item.label}
											</button>
										</li>
									))}
								</ul>,
								document.body,
							)}
					</div>
				</div>
				<div className="workout-folder-card-count">
					<span className="workout-folder-card-count-text">
						{t("allTemplates.workoutFolders.workoutCount", {
							count: workoutCount,
							defaultValue_one: "{{count}} workout",
							defaultValue_other: "{{count}} workouts",
						})}
					</span>
					{canToggleDescription && (
						<button
							type="button"
							className="workout-folder-card-description-toggle"
							onClick={(e) => {
								e.stopPropagation();
								onToggleDescriptionExpanded?.();
							}}
							onKeyDown={(e) => {
								// Enter/Space bubbles as keydown regardless of the
								// click stopPropagation above; stop it here too so it
								// doesn't also trigger the card's handleCardKeyDown
								// (which would navigate into the folder).
								e.stopPropagation();
							}}
							aria-expanded={descriptionExpanded}
						>
							{descriptionExpanded
								? t(
										"allTemplates.workoutFolders.descriptionCollapse",
										"Show less",
									)
								: t(
										"allTemplates.workoutFolders.descriptionExpand",
										"Show more",
									)}
						</button>
					)}
				</div>
			</div>
			<ConfirmationModal
				title={t(
					"allTemplates.workoutFolders.deleteConfirmTitle",
					"Delete Folder",
				)}
				contentText={t(
					"allTemplates.workoutFolders.deleteConfirmText",
					"Are you sure you want to delete this folder? Workouts inside will become uncategorized.",
				)}
				cancelButtonText={t("allTemplates.workoutFolders.cancel", "Cancel")}
				cancelButtonAction={() => setConfirmDelete(false)}
				confirmButtonText={t(
					"allTemplates.workoutFolders.deleteConfirmAction",
					"Delete Folder",
				)}
				confirmButtonAction={confirmDeleteFolder}
				isOpen={confirmDelete}
				icon="delete"
			/>
		</>
	);
};

WorkoutFolderCard.propTypes = {
	folder: PropTypes.shape({
		_id: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		description: PropTypes.string,
		workoutIds: PropTypes.array,
	}).isRequired,
	workoutCount: PropTypes.number,
	descriptionExpanded: PropTypes.bool,
	onToggleDescriptionExpanded: PropTypes.func,
};

export default WorkoutFolderCard;
